import { type Except, isEmpty, type ValidatedKey } from '@festina-life/core'
import { useField } from 'formik'
import type { ReactNode } from 'react'

import {
  RadioGroup,
  type RadioGroupProps,
} from '../../forms/radioGroup/RadioGroup'
import { useFormikErrorWithState } from '../../hooks/useFormikErrorWithState'
import { useFormikIsDisabled } from '../../hooks/useFormikIsDisabled'
import { useFormikIsRequired } from '../../hooks/useFormikIsRequired'
import { useExistingValuesContext } from '../../theme/existingValuesContext'
import {
  handleDefaultValueClick,
  handleExistingValueClick,
  handleOnBlur,
} from '../formikUtils/formikUtils'
import { getWrapperProps } from '../formikUtils/getWrapperProps'
import { useSimpleValueDifferenceCheck } from '../formikUtils/useSimpleValueDifferenceCheck'
import { withFormikInputDiffing } from '../formikUtils/withFormikInputDiffing'
import type { FormikInputPropsForWrapper } from '../types'

export type FormikRadioProps<T> = Except<
  RadioGroupProps<T>,
  'onChange' | 'onBlur' | 'wrapperProps'
> & {
  name: ValidatedKey
  defaultValue?: T
  onBlur?: (value: T | undefined) => void
} & Except<FormikInputPropsForWrapper, 'validationText'>

const FormikRadioGroupInternal = <T,>({
  name,
  options,
  description,
  label,
  defaultValue,
  onDefaultValueClick,
  isRequired,
  onBlur,
  isDisabled,
  hasRightColumnAlways,
  className,
  existingValueConfig,
  ...restProps
}: FormikRadioProps<T>) => {
  const [field, meta, { setValue }] = useField(name)
  const { isInError, error } = useFormikErrorWithState(meta)
  const isFieldDisabled = useFormikIsDisabled(
    typeof isDisabled === 'boolean' ? isDisabled : undefined,
  )
  const existingValue = useExistingValuesContext()?.getExistingValue?.(name) as
    | T
    | undefined

  const toRenderString = (
    value: T | undefined,
    renderFn: ((item: T) => ReactNode) | undefined,
  ) => {
    if (isEmpty(value)) {
      return value
    }

    return renderFn ? renderFn(value) : `${value}`
  }

  const renderDefault = toRenderString(defaultValue, restProps.renderFn)
  const renderExistingValue = toRenderString(
    existingValue as T,
    restProps.renderFn,
  )

  const inputValueDifferenceFromExistingValue =
    useSimpleValueDifferenceCheck(name)

  const wrapperProps = getWrapperProps({
    label,
    existingValue: renderExistingValue,
    existingValueConfig,
    inputValueDifferenceFromExistingValue,
    commonWrapperProps: {
      isRequired: useFormikIsRequired(name, isRequired),
      description,
      defaultValue: renderDefault,
      onDefaultValueClick: () =>
        handleDefaultValueClick({
          defaultValue,
          onBlur,
          onDefaultValueClick,
        }),
      onExistingValueClick: () =>
        handleExistingValueClick({
          existingValue,
          setValue,
          onBlur,
        }),
      htmlFor: name,
      error,
      hasRightColumnAlways,
      className,
    },
  })

  return (
    <RadioGroup
      wrapperProps={wrapperProps}
      id={name}
      isInError={isInError}
      options={options}
      isDisabled={isFieldDisabled}
      {...field}
      {...restProps}
      onChange={setValue}
      onBlur={(event) => handleOnBlur({ event, field, onBlur })}
    />
  )
}

export const FormikRadioGroup = withFormikInputDiffing(FormikRadioGroupInternal)
